<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { NButton, NInput, NPopconfirm, NSelect } from 'naive-ui'
import type { AgentPrompt } from '../stores/agent_prompts'

// One prompt's card in the Prompt Library. Ten
// full-height edit forms stacked on the page proved overwhelming in
// practice (every row always showing both inputs at full size made
// scanning for a specific prompt slow and scroll-heavy) — so each row now
// defaults to a compact single-line summary and expands to the editable
// form on demand. Scope changes and reordering stay immediate (single
// discrete actions, no edit mode needed); label/text edit through a local
// draft + explicit Save/Cancel, same as before.
//
// `onUpdate` is a promise-returning prop (not an emit) because `draft.saving`
// needs to reflect the ACTUAL in-flight save — a plain `emit` is fire-and-
// forget, so awaiting it would resolve instantly and the Save button's
// spinner would never show, even though the real API call is still pending.
const props = defineProps<{
  prompt: AgentPrompt
  // naive-ui option values can't be `null` (see PromptLibrary.vue's
  // GLOBAL_SCOPE comment) — `''` is the Global sentinel here too.
  scopeOptions: Array<{ label: string; value: string }>
  isFirst: boolean
  isLast: boolean
  onUpdate: (id: number, params: { label?: string; text?: string }) => Promise<void>
}>()

const emit = defineEmits<{
  scopeChange: [id: number, repoSlug: string | null]
  destroy: [id: number]
  moveUp: [id: number]
  moveDown: [id: number]
}>()

const expanded = ref(false)

const draft = reactive({
  label: props.prompt.label,
  text: props.prompt.text,
  saving: false,
})

// Gates the Save button — avoids a no-op API round-trip when the fields
// still match what's already persisted (e.g. the user only opened the
// card to read it, or clicked Edit twice).
const isDirty = computed(() => draft.label !== props.prompt.label || draft.text !== props.prompt.text)

function toggleExpanded(): void {
  // Re-sync the draft from the current prop on every expand — `draft` is
  // otherwise only seeded once at setup, so if the underlying prompt changed
  // while this row sat collapsed (e.g. edited from another tab, then this
  // tab's fetchAll() refreshed it), re-opening would show stale content and
  // Save would silently clobber the newer value with it.
  if (!expanded.value) {
    draft.label = props.prompt.label
    draft.text = props.prompt.text
  }
  expanded.value = !expanded.value
}

function cancel(): void {
  draft.label = props.prompt.label
  draft.text = props.prompt.text
  expanded.value = false
}

async function save(): Promise<void> {
  if (!isDirty.value) return
  draft.saving = true
  try {
    await props.onUpdate(props.prompt.id, { label: draft.label, text: draft.text })
    expanded.value = false
  } finally {
    draft.saving = false
  }
}

function onScopeChange(value: string): void {
  emit('scopeChange', props.prompt.id, value || null)
}
</script>

<template>
  <li
    class="rounded border border-white/10 bg-black/20 px-4 flex flex-col gap-3"
    :class="expanded ? 'py-4' : 'py-2'"
    :data-testid="`prompt-row-${prompt.id}`"
  >
    <div class="flex items-center gap-2">
      <NButton
        size="tiny"
        quaternary
        title="Move up"
        :disabled="isFirst"
        :data-testid="`prompt-move-up-${prompt.id}`"
        @click="emit('moveUp', prompt.id)"
        >↑</NButton
      >
      <NButton
        size="tiny"
        quaternary
        title="Move down"
        :disabled="isLast"
        :data-testid="`prompt-move-down-${prompt.id}`"
        @click="emit('moveDown', prompt.id)"
        >↓</NButton
      >
      <NSelect
        :value="prompt.repo_slug || ''"
        :options="scopeOptions"
        size="small"
        class="max-w-40 shrink-0"
        :data-testid="`prompt-scope-${prompt.id}`"
        @update:value="onScopeChange"
      />
      <span
        v-if="!expanded"
        class="flex-1 min-w-0 truncate text-sm text-white/90"
        :data-testid="`prompt-label-display-${prompt.id}`"
        :title="prompt.label"
        >{{ prompt.label }}</span
      >
      <div class="flex-1" v-else />
      <NButton size="small" quaternary :data-testid="`prompt-edit-${prompt.id}`" @click="toggleExpanded">{{
        expanded ? 'Collapse' : 'Edit'
      }}</NButton>
      <NPopconfirm
        positive-text="Remove"
        negative-text="Cancel"
        :positive-button-props="{ type: 'error' }"
        @positive-click="emit('destroy', prompt.id)"
      >
        <template #trigger>
          <NButton size="small" tertiary type="error" :data-testid="`prompt-delete-${prompt.id}`"> Remove </NButton>
        </template>
        Remove this prompt?
      </NPopconfirm>
    </div>

    <template v-if="expanded">
      <label class="flex flex-col gap-1 text-xs text-white/60">
        Label
        <NInput v-model:value="draft.label" :data-testid="`prompt-label-${prompt.id}`" />
      </label>
      <label class="flex flex-col gap-1 text-xs text-white/60">
        Text
        <NInput v-model:value="draft.text" type="textarea" :rows="3" :data-testid="`prompt-text-${prompt.id}`" />
      </label>

      <div class="flex items-center justify-end gap-2">
        <NButton size="small" :data-testid="`prompt-cancel-${prompt.id}`" @click="cancel">Cancel</NButton>
        <NButton
          size="small"
          type="primary"
          :disabled="!isDirty"
          :loading="draft.saving"
          :data-testid="`prompt-save-${prompt.id}`"
          @click="save"
          >Save</NButton
        >
      </div>
    </template>
  </li>
</template>
